import React from 'react';
import { useRef } from 'react';
import { Box, Typography } from '@mui/material';

import {
  SectionContent,
  GoBackButton,
  ImageTextOverlay,
  PageHeader,
  ContactLink
} from '../../components';
import { TRADING_BUSINESS_CONTENT } from '../../data';
import ROUTES from '../../config/routes';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import wallpaperImage02 from '../../assets/images/strategies/wallpaper-image-02.jpg';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const TradingBusinessVisionPage = ({ setRefs }) => {
  // Create refs at the top level
  const overviewRef = useRef(null);
  const peopleAtCoreRef = useRef(null);
  const purposeDrivenRef = useRef(null);
  const automationRef = useRef(null);
  const longTermMindsetRef = useRef(null);
  const ourPrinciplesRef = useRef(null);
  const exceptionalPeopleRef = useRef(null);
  const humilityRef = useRef(null);
  const entrepreneurialRef = useRef(null);
  const collaborativeRef = useRef(null);

  // Organize refs into an object
  const refs = React.useMemo(
    () => ({
      overview: overviewRef,
      peopleAtCore: peopleAtCoreRef,
      purposeDriven: purposeDrivenRef,
      automation: automationRef,
      longTermMindset: longTermMindsetRef,
      ourPrinciples: ourPrinciplesRef,
      exceptionalPeople: exceptionalPeopleRef,
      humility: humilityRef,
      entrepreneurial: entrepreneurialRef,
      collaborative: collaborativeRef
    }),
    []
  );

  // Update global refs if setRefs function is provided
  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  return (
    <>
      <SEO
        title={seoData.tradingBusiness.title}
        content={seoData.tradingBusiness.content}
        keywords={seoData.tradingBusiness.keywords}
        canonical={seoData.tradingBusiness.canonical}
        ogImage={seoData.tradingBusiness.ogImage}
      />
      {/* Back Button */}}
      <GoBackButton
        backTo={ROUTES.STRATEGIES.ROOT}
        backText='Back to Strategies'
      />
      <PageHeader
        id='hero'
        title={TRADING_BUSINESS_CONTENT.TITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />
      <ImageTextOverlay
        imageSrc={wallpaperImage02}
        text={TRADING_BUSINESS_CONTENT.INTRO[0]}
        height={{ xs: '200px', sm: '200px', md: '200px', lg: '200px' }}
        boxSx={{
          width: '100%',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          textAlign: 'center'
        }}
        textSx={{
          fontSize: { xs: '1.07rem', sm: '1.15rem', md: '1.3rem' },
          lineHeight: 1.6,
          color: THEME.COLORS.NEUTRAL_WHITE,
          px: { xs: 2, sm: 3, md: 5 },
          zIndex: 2,
          width: '100%',
          maxWidth: { xs: '100%', md: '80%' }
        }}
      />
      <SectionContent
        id='overview'
        ref={refs.overview}
        initialSpacing={false}
        text={[
          {
            text: TRADING_BUSINESS_CONTENT.INTRO[1],
            marginBottom: SECTION_SPACING,
            marginTop: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* People at the Core Section */}
      <SectionContent
        id='peopleAtCore'
        ref={refs.peopleAtCore}
        title={TRADING_BUSINESS_CONTENT.SECTIONS.PEOPLE_CORE.TITLE}
        titleVariant='TitleSmall' // Changed from TitleMedium to TitleSmall for sub-sections
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: TRADING_BUSINESS_CONTENT.SECTIONS.PEOPLE_CORE.TEXT,
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Purpose-Driven Opportunity Section */}
      <SectionContent
        id='purposeDriven'
        ref={refs.purposeDriven}
        title={TRADING_BUSINESS_CONTENT.SECTIONS.PURPOSE_DRIVEN.TITLE}
        titleVariant='TitleSmall' // Changed from TitleMedium to TitleSmall
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: TRADING_BUSINESS_CONTENT.SECTIONS.PURPOSE_DRIVEN.TEXT,
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Automation as a Catalyst Section */}
      <SectionContent
        id='automation'
        ref={refs.automation}
        title={TRADING_BUSINESS_CONTENT.SECTIONS.AUTOMATION.TITLE}
        titleVariant='TitleSmall' // Changed from TitleMedium to TitleSmall
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TRADING_BUSINESS_CONTENT.SECTIONS.AUTOMATION.TEXT.map(
          (text, index, array) => ({
            text,
            marginBottom:
              index === array.length - 1 ? SECTION_SPACING : ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />
      {/* Built with a Long-Term Mindset Section */}
      <SectionContent
        id='longTermMindset'
        ref={refs.longTermMindset}
        title={TRADING_BUSINESS_CONTENT.SECTIONS.LONG_TERM.TITLE}
        titleVariant='TitleSmall' // Changed from TitleMedium to TitleSmall
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: TRADING_BUSINESS_CONTENT.SECTIONS.LONG_TERM.TEXT,
            textAlign: 'center',
            padding: `${THEME.SPACING.LARGE} ${THEME.SPACING.XL}`,
            backgroundColor: THEME.COLORS.PRIMARY_BLUE,
            color: THEME.COLORS.NEUTRAL_WHITE, // Removed hoverColor property
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Our Principles Section */}
      <SectionContent
        id='ourPrinciples'
        ref={refs.ourPrinciples}
        title={TRADING_BUSINESS_CONTENT.SECTIONS.OUR_PRINCIPLES.TITLE}
        titleVariant='TitleSmall' // Changed from TitleMedium to TitleSmall
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TRADING_BUSINESS_CONTENT.SECTIONS.OUR_PRINCIPLES.TEXT.map(
          (text, index, array) => ({
            text,
            marginBottom:
              index === array.length - 1 ? SUBSECTION_SPACING : ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />
      {/* Exceptional People, Strong Culture Section - Principle subsection */}
      <Box sx={{ mt: ELEMENT_SPACING, mb: ELEMENT_SPACING }}>
        <SectionContent
          id='exceptionalPeople'
          ref={refs.exceptionalPeople}
          title={TRADING_BUSINESS_CONTENT.SECTIONS.PRINCIPLES[0].TITLE}
          titleVariant='TitleSmallBlue' // Using BodyLarge for principle subsections (sub-sub-titles)
          titleColor={THEME.COLORS.PRIMARY_BLUE}
          titleMargin={`0 0 ${ELEMENT_SPACING} 0`}
          initialSpacing={false}
          text={TRADING_BUSINESS_CONTENT.SECTIONS.PRINCIPLES[0].TEXT.map(
            (text, index, array) => ({
              text,
              marginBottom:
                index === array.length - 1
                  ? ELEMENT_SPACING * 3
                  : ELEMENT_SPACING,
              fontSize: '1.3rem'
            })
          )}
        />
      </Box>
      {/* Humility, Respect, and Trust Section - Principle subsection */}
      <Box sx={{ mt: ELEMENT_SPACING, mb: ELEMENT_SPACING }}>
        <SectionContent
          id='humility'
          ref={refs.humility}
          title={TRADING_BUSINESS_CONTENT.SECTIONS.PRINCIPLES[1].TITLE}
          titleVariant='TitleSmallBlue' // Using BodyLarge for principle subsections
          titleColor={THEME.COLORS.PRIMARY_BLUE}
          titleMargin={`0 0 ${ELEMENT_SPACING} 0`}
          initialSpacing={false}
          text={TRADING_BUSINESS_CONTENT.SECTIONS.PRINCIPLES[1].TEXT.map(
            (text, index, array) => ({
              text,
              marginBottom:
                index === array.length - 1
                  ? ELEMENT_SPACING * 3
                  : ELEMENT_SPACING,
              fontSize: '1.3rem'
            })
          )}
        />
      </Box>
      {/* Entrepreneurial Mindset Section - Principle subsection */}
      <Box sx={{ mt: ELEMENT_SPACING, mb: ELEMENT_SPACING }}>
        <SectionContent
          id='entrepreneurial'
          ref={refs.entrepreneurial}
          title={TRADING_BUSINESS_CONTENT.SECTIONS.PRINCIPLES[2].TITLE}
          titleVariant='TitleSmallBlue' // Using BodyLarge for principle subsections
          titleColor={THEME.COLORS.PRIMARY_BLUE}
          titleMargin={`0 0 ${ELEMENT_SPACING} 0`}
          initialSpacing={false}
          text={TRADING_BUSINESS_CONTENT.SECTIONS.PRINCIPLES[2].TEXT.map(
            (text, index, array) => ({
              text,
              marginBottom:
                index === array.length - 1
                  ? ELEMENT_SPACING * 3
                  : ELEMENT_SPACING,
              fontSize: '1.3rem'
            })
          )}
        />
      </Box>
      {/* A Collaborative Spirit Section - Principle subsection */}
      <Box sx={{ mb: SUBSECTION_SPACING }}>
        <SectionContent
          id='collaborative'
          ref={refs.collaborative}
          title={TRADING_BUSINESS_CONTENT.SECTIONS.PRINCIPLES[3].TITLE}
          titleVariant='TitleSmallBlue' // Using BodyLarge for principle subsections
          titleColor={THEME.COLORS.PRIMARY_BLUE}
          titleMargin={`0 0 ${ELEMENT_SPACING} 0`}
          initialSpacing={false}
          text={TRADING_BUSINESS_CONTENT.SECTIONS.PRINCIPLES[3].TEXT.map(
            (text, index, array) => ({
              text,
              marginBottom:
                index === array.length - 1 ? SECTION_SPACING : ELEMENT_SPACING,
              fontSize: '1.3rem'
            })
          )}
        />
      </Box>
      {/* Footer note with separator line */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          marginTop: SECTION_SPACING,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {TRADING_BUSINESS_CONTENT.FOOTER_NOTE}
        </Typography>
        <Box mt={ELEMENT_SPACING}>
          <Typography
            variant='body2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '1.2rem',
              color: THEME.COLORS.PRIMARY_BLUE,
              lineHeight: 1.6
            }}
          >
            Talk to an Expert –&nbsp;
            <ContactLink
              style={{
                color: THEME.COLORS.NEUTRAL_GRAY,
                textDecoration: 'underline',
                fontSize: '1rem'
              }}
            >
              Schedule Your meeting
            </ContactLink>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default TradingBusinessVisionPage;
